import type { CpuRun } from "../../api/types";
import { taskSegments } from "../../lib/cpu";

interface TaskMeterProps {
  run: CpuRun;
}

/**
 * The five counters of a fan-out, as one bar.
 *
 * It is the `.util` family `CoresMeter` established, stacked: a run of a
 * thousand tasks is 847 finished, 3 failed, 45 running and 105 waiting at
 * once, and no single number and no single state can stand for that. The
 * segments are keyed on `data-segment`, never on `data-tone` — the tone union
 * is five members with two meanings each across the console, and a sixth
 * keying of it here would be a colour vocabulary nobody could keep in step.
 *
 * **The sentence above it is what carries the meaning.** `taskSummary` says
 * the same counts in words, in the trace's own wording, and it is read first
 * by a person and by a screen reader alike — so the bar is decoration of that
 * sentence and is hidden from assistive technology rather than repeating it in
 * a second dialect.
 *
 * Args:
 *     run: The run as served by the API.
 *
 * Returns:
 *     The stacked bar, or nothing at all for a run with no tasks to divide.
 */
export function TaskMeter({ run }: TaskMeterProps): JSX.Element | null {
  const segments = taskSegments(run);
  if (segments.length === 0) {
    return null;
  }

  return (
    <div className="util task-meter" aria-hidden="true">
      <div className="util__track task-meter__track">
        {segments.map((segment) => (
          <div
            key={segment.segment}
            className="util__fill task-meter__segment"
            data-segment={segment.segment}
            data-count={segment.count}
            style={{ width: `${segment.percent}%` }}
          />
        ))}
      </div>
      <span className="util__value mono">
        {run.tasks_succeeded}/{run.tasks}
      </span>
    </div>
  );
}
